import { useState, useEffect, useCallback } from 'react';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
  Badge, Button, Input, Label, Switch,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
  Spinner,
} from '@rnd-payplus/alloy-react';
import { RefreshCw, Search } from 'lucide-react';
import { FilterButton, SearchFilterButton } from '../components/FilterButton';
import { KPICard } from '@rnd-payplus/alloy-react/patterns';
import { usePageShell } from '../contexts/PageShellContext';

interface LogEntry {
  id: string;
  created_at: string;
  level: string;
  service: string;
  message: string;
  trace_id: string | null;
}

interface TraceSpan {
  id: string;
  span_id: string;
  parent_span_id: string | null;
  service: string;
  operation: string;
  status: string;
  duration_ms: number | null;
  started_at: string;
  ended_at: string | null;
}

function getTimeRangeFrom(key: string): string {
  const offsets: Record<string, number> = {
    '1h': 3600000,
    '6h': 6 * 3600000,
    '24h': 24 * 3600000,
    '7d': 7 * 86400000,
  };
  return new Date(Date.now() - (offsets[key] ?? 3600000)).toISOString();
}

const getLevelVariant = (level: string): 'destructive' | 'secondary' | 'default' | 'outline' => {
  if (level === 'error') return 'destructive';
  if (level === 'warn') return 'secondary';
  if (level === 'info') return 'default';
  return 'outline';
};

const ObservabilityPage = () => {
  const [tabValue, setTabValue] = useState('logs');

  // Log state
  const [levelFilter, setLevelFilter] = useState<string[]>([]);
  const [serviceFilter, setServiceFilter] = useState<string[]>([]);
  const [timeFilter, setTimeFilter] = useState<string[]>(['1h']);
  const [searchQuery, setSearchQuery] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [serviceOptions, setServiceOptions] = useState<string[]>([]);

  // Trace state
  const [traceId, setTraceId] = useState('');
  const [spans, setSpans] = useState<TraceSpan[]>([]);
  const [traceLoading, setTraceLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const timeRange = timeFilter[0] || '1h';
      const params = new URLSearchParams({ limit: '100', from: getTimeRangeFrom(timeRange) });
      if (levelFilter.length === 1) params.set('level', levelFilter[0]);
      if (serviceFilter.length === 1) params.set('service', serviceFilter[0]);
      if (searchQuery) params.set('search', searchQuery);

      const res = await fetch(`/api/logs?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setLogs(data.data ?? []);
      setServiceOptions(data.services ?? []);
    } catch (err) {
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  }, [levelFilter, serviceFilter, searchQuery, timeFilter]);

  useEffect(() => {
    if (tabValue === 'logs') fetchLogs();
  }, [tabValue, fetchLogs]);

  useEffect(() => {
    if (!autoRefresh || tabValue !== 'logs') return;
    const id = setInterval(fetchLogs, 10000);
    return () => clearInterval(id);
  }, [autoRefresh, tabValue, fetchLogs]);

  const fetchTrace = async () => {
    if (!traceId.trim()) return;
    setTraceLoading(true);
    try {
      const res = await fetch(`/api/traces/${encodeURIComponent(traceId.trim())}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSpans(data.data ?? []);
    } catch (err) {
      console.error('Error fetching trace:', err);
      setSpans([]);
    } finally {
      setTraceLoading(false);
    }
  };

  const totalDuration = spans.reduce((sum, s) => sum + (s.duration_ms ?? 0), 0);

  const totalLogs = logs.length;
  const errorLogs = logs.filter(l => l.level === 'error').length;
  const uniqueServices = new Set(logs.map(l => l.service)).size;
  const totalSpans = spans.length;

  usePageShell({
    title: 'Observability',
    secondaryActions: (
      <>
        <div className="flex items-center gap-2">
          <Switch checked={autoRefresh} onCheckedChange={setAutoRefresh} />
          <Label className="text-sm">Auto-refresh</Label>
        </div>
        <Button variant="outline" onClick={fetchLogs} disabled={loading}>
          <RefreshCw className={`mr-2 size-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </>
    ),
    breadcrumbs: [
      { label: 'Home', href: '/dashboard' },
      { label: 'Observability' },
    ],
  });

  return (
    <div className="space-y-5">
      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4">
        <KPICard title="Total Logs" value={totalLogs} subtitle="Entries" />
        <KPICard title="Errors" value={errorLogs} subtitle="Log Entries" />
        <KPICard title="Services" value={uniqueServices} subtitle="Unique" />
        <KPICard title="Traces" value={totalSpans} subtitle="Spans" />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="logs" value={tabValue} onValueChange={setTabValue}>
        <TabsList className="w-full">
          <TabsTrigger className="flex-1" value="logs">Logs</TabsTrigger>
          <TabsTrigger className="flex-1" value="traces">Traces</TabsTrigger>
        </TabsList>

        {/* Logs Tab */}
        <TabsContent value="logs" className="space-y-4 mt-5">
          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <FilterButton
              label="Level"
              options={['error', 'warn', 'info', 'debug']}
              selected={levelFilter}
              onChange={setLevelFilter}
            />
            <FilterButton
              label="Service"
              options={serviceOptions}
              selected={serviceFilter}
              onChange={setServiceFilter}
            />
            <FilterButton
              label="Time Range"
              options={['1h', '6h', '24h', '7d']}
              selected={timeFilter}
              onChange={setTimeFilter}
            />
            <SearchFilterButton
              label="Search"
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>

          <p className="text-xs text-muted-foreground">{logs.length} entries</p>

          {/* Log Table */}
          <div className="rounded-md border max-h-[520px] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Level</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Trace ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No logs found. Try expanding the time range or triggering an API call.
                    </TableCell>
                  </TableRow>
                )}
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="whitespace-nowrap text-xs">
                      {new Date(log.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getLevelVariant(log.level)}>
                        {log.level.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell>{log.service}</TableCell>
                    <TableCell className="max-w-[400px] truncate">{log.message}</TableCell>
                    <TableCell className="font-mono text-xs">{log.trace_id ?? '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Traces Tab */}
        <TabsContent value="traces" className="space-y-5 mt-5">
          <div>
            <h2 className="text-base font-medium leading-6 mb-3">Trace Lookup</h2>
            <div className="flex gap-2">
              <Input
                className="flex-1"
                placeholder="Enter trace ID..."
                value={traceId}
                onChange={(e) => setTraceId(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchTrace()}
              />
              <Button onClick={fetchTrace} disabled={traceLoading || !traceId.trim()}>
                {traceLoading ? (
                  <Spinner className="mr-2 size-4" />
                ) : (
                  <Search className="mr-2 size-4" />
                )}
                Search
              </Button>
            </div>
          </div>

          {spans.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {spans.length} spans &middot; {totalDuration}ms total
              </p>
              {spans.map((span) => {
                const pct =
                  totalDuration > 0 ? ((span.duration_ms ?? 0) / totalDuration) * 100 : 0;
                return (
                  <div
                    key={span.id}
                    className={span.parent_span_id ? 'pl-6' : ''}
                  >
                    <div className="flex justify-between mb-1">
                      <p className="text-sm">
                        <span className="font-medium">{span.service}</span>
                        <span className="text-muted-foreground"> &middot; {span.operation}</span>
                      </p>
                      <div className="flex gap-2 items-center">
                        <Badge variant={span.status === 'ok' ? 'default' : 'destructive'}>
                          {span.status}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {span.duration_ms ?? '-'}ms
                        </span>
                      </div>
                    </div>
                    <div className="h-2 bg-muted rounded">
                      <div
                        className={`h-full rounded ${span.status === 'ok' ? 'bg-green-500' : 'bg-destructive'}`}
                        style={{ width: `${pct}%`, minWidth: '4px' }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {spans.length === 0 && traceId && !traceLoading && (
            <p className="text-sm text-muted-foreground">
              No spans found for trace ID &ldquo;{traceId}&rdquo;. Spans are written when dataflow
              operations complete.
            </p>
          )}

          {!traceId && !traceLoading && spans.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Enter a trace ID above to view the distributed trace waterfall. Trace IDs are shown in
              the Logs tab.
            </p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ObservabilityPage;
